import React from 'react'
import PropTypes from 'prop-types'
import LazyLoad from 'vanilla-lazyload'
import Turbolinks from 'turbolinks'
import { helpers, urlHelpers, prismicHelpers } from '../../helpers'

/* doc
---
title: Promo Banner
name: promobanner
category: Javascript - Components
---

A promo banner used to display a single promotion image. Handles analytical
tracking of promo. Data is typically received from Prismic.

```html_example
  <%= react_component 'PromoBanner', { promo: promo_data } %>
```
*/
class PromoBanner extends React.Component {
  constructor(props) {
    super(props)

    this.uid = helpers.generateID('promobanner')

    this.getPromoLink = this.getPromoLink.bind(this)
    this.onClickHandler = this.onClickHandler.bind(this)
    this.renderImage = this.renderImage.bind(this)
  }

  componentDidMount() {
    new LazyLoad({
      data_src: 'original',
      data_srcset: 'original-set',
      elements_selector: `#${this.uid} img`,
    })
  }

  /**
  * On click event handler for when a user clicks the banner. Tracks
  * the promo as having been clicked and then takes the user to the promo's
  * destination.
  */
  onClickHandler(e) {
    e.preventDefault()
    $.track('promo click', {
      id: this.props.promo.promoId,
      name: this.props.promo.promoName,
      creative: this.props.promo.creativeName,
      position: this.props.promo.promoPosition,
    }, () => {
      Turbolinks.visit(this.getPromoLink())
    })
  }

  /**
  * Formats the promotion's link to include a `ref` query param matching the
  * promotion's `creativeName`.
  */
  getPromoLink() {
    const creativeName = this.props.promo.creativeName


    const url = this.props.promo.link

    if (!creativeName) {
      return url
    }

    return urlHelpers.addRef(url, creativeName)
  }

  /**
  * Renders the promo banner and sets its background color if one is specified.
  */
  renderImage() {
    let figureStyles = {}


    let imageURL = this.props.promo.image.url

    if (this.props.promo.backgroundColor) {
      figureStyles = {
        backgroundColor: `#${this.props.promo.backgroundColor}`,
      }
    }

    // If there's a more appropriate size image available then use that one.
    if (this.props.promo.type && this.props.promo.type in this.props.promo.image.views) {
      imageURL = this.props.promo.image.views[this.props.promo.type].url
    }

    return (
      <figure style={figureStyles}>
        <img
          className="is-lazy fade-in"
          data-original={imageURL}
          data-original-set={prismicHelpers.formatSrcset(this.props.promo.image)}
          alt={this.props.promo.image.alt}
        />
      </figure>
    )
  }

  render() {
    const ratioClass = this.props.promo.type ? `is-${this.props.promo.type}` : ''


    const sizeClass = this.props.promo.size ? this.props.promo.size : ''


    const viewableClass = prismicHelpers.formatViewClasses(this.props.promo)

    return (
      <a
        className={`promo-banner ${ratioClass} ${sizeClass} ${viewableClass}`}
        href={this.getPromoLink()}
        aria-label={this.props.promo.title}
        id={this.uid}
        onClick={this.onClickHandler}
      >
        { this.renderImage() }
      </a>
    )
  }
}

PromoBanner.propTypes = {
  promo: PropTypes.object.isRequired,
}

export default PromoBanner
